import { useIntl } from 'react-intl';
import { Modal, Form, Empty, Typography, Divider, Button } from 'antd';
import { ContactType } from '@/core/domain/_shared/ContactType';
import {
  CreateCustomerRequest,
  Customer,
} from '@/core/domain/people/Customers/Customers.types';
import {
  useCreateCustomer,
  useUpdateCustomer,
} from '@/core/domain/people/Customers/Customers.hooks';
import { CreateCustomerAddressRequest } from '@/core/domain/people/CustomerAddresses/CustomerAddresses.types';
import { AxiosError } from 'axios';
import CustomerSimplifiedForm, {
  CustomerSimplifiedFormData,
} from '../CustomerSimplifiedForm';
import { CreateCustomerVehicleRequest } from '@/core/domain/people/CustomerVehicles/CustomerVehicles.types';
import {
  useCreateCustomerVehicle,
  useUpdateCustomerVehicle,
} from '@/core/domain/people/CustomerVehicles/CustomerVehicles.hooks';
import { useState } from 'react';
import { FaCar } from 'react-icons/fa';
import { Group } from '@/components/_common';
import { useUpdateWorkOrder } from '@/core/domain/workorder/WorkOrders/WorkOrders.hooks';
import { useWorkorderDetailContext } from '@/components/Movements/WorkOrders/Details/WorkorderDetailProvider';
import notificationError from '@/utils/functions/notificationError';
import { CreateContactRequest } from '@/core/domain/people/_shared/Contact';
import flatContacts from '@/utils/functions/flatContacts';
import moment from 'moment';
import { DateTimeFormat } from '@/utils/moment';
import CustomerContactCollapse from '../CustomerContactCollapse';
import VehicleFormWrapper from '@/components/_common/_VehicleModal/VehicleFormWrapper';
import { VehicleFormValues } from '@/components/_common/_VehicleModal/VehicleModal.types';
import { parseAccessoryId } from '@/components/_common/_VehicleModal/getAccessoryId';
import { useCustomerContacts } from '@/core/domain/people/CustomerContacts/CustomerContacts.hooks';
import { uploadArchive } from '@/core/domain/archive/Archive/Archive.service';

const { Title } = Typography;

interface Props {
  visible: boolean;
  onCancel: VoidFunction;
  onConfirm: VoidFunction;
}

function CustomerWorkorderCreateModal({ visible, onCancel, onConfirm }: Props) {
  const intl = useIntl();
  const [customerForm] = Form.useForm<CustomerSimplifiedFormData>();
  const [vehicleForm] = Form.useForm<VehicleFormValues>();
  const [customerId, setCustomerId] = useState<number>();
  const [vehicleId, setVehicleId] = useState<number>();
  const [vehicleDisabled, setVehicleDisabled] = useState(false);

  const hasVehicleData = Object.values(
    Form.useWatch([], vehicleForm) || {},
  ).some((value) => !!value);

  const {
    workorder: { data: workorder },
  } = useWorkorderDetailContext();

  const createCustomer = useCreateCustomer();
  const updateCustomer = useUpdateCustomer();
  const { data: customerContacts } = useCustomerContacts(customerId);

  const createVehicle = useCreateCustomerVehicle(customerId || 0);
  const updateVehicle = useUpdateCustomerVehicle(customerId || 0);

  const updateWorkorder = useUpdateWorkOrder(workorder?.id || 0);

  const handleCustomerSubmit = async () => {
    try {
      const {
        address,
        email: formEmail,
        phone: formPhone,
        ...values
      } = await customerForm.validateFields();

      let obj: CreateCustomerRequest = {
        ...values,
        cpfCnpj: values.cpfCnpj || undefined,
        active: true,
        birthDate: moment(values.birthDate).format(DateTimeFormat),
        recommendation: values.recommendation?.id
          ? values.recommendation
          : undefined,
      };

      if (
        address &&
        address.cityId &&
        address.number &&
        address.street &&
        address.zipCode
      ) {
        const customerAddress: CreateCustomerAddressRequest = {
          complement: address.complement,
          district: address.district,
          cityId: address.cityId,
          number: address.number,
          street: address.street,
          zipCode: address.zipCode,
          delivery: true,
          main: true,
        };

        obj = { ...obj, addresses: [customerAddress] };
      }

      const contacts: CreateContactRequest[] = [];

      if (formEmail) {
        contacts.push({
          main: true,
          active: true,
          contactType: ContactType.Email,
          description: 'E-Mail',
          value: formEmail,
          id: flatContacts(customerContacts).find(
            (contact) => contact.contactType === ContactType.Email,
          )?.id,
        });
      }

      if (formPhone) {
        contacts.push({
          main: true,
          active: true,
          contactType: ContactType.Phone,
          description: 'Telefone',
          value: formPhone,
          id: flatContacts(customerContacts).find(
            (contact) => contact.contactType === ContactType.Phone,
          )?.id,
        });
      }

      let result: Customer;

      if (customerId) {
        if (contacts.length) {
          const newContacts =
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            customerContacts?.reduce<any>((prev, curr) => {
              return [
                ...prev,
                {
                  ...curr,
                  detailList: curr.detailList.map((item) => {
                    const finded = contacts.find((c) => c.id === item.id);
                    if (finded) {
                      return { ...item, ...finded };
                    }
                    return item;
                  }),
                },
              ];
            }, []) || [];

          obj = { ...obj, contacts: newContacts };
        }

        result = await updateCustomer.mutateAsync({
          obj,
          id: customerId,
        });
      } else {
        if (contacts.length) {
          obj = {
            ...obj,
            contacts: [{ detailList: contacts, name: obj.name, main: true }],
          };
        }

        result = await createCustomer.mutateAsync({
          ...obj,
          rentalCompany: false,
        });
      }

      setCustomerId(result.id);
    } catch (err) {
      const error = err as AxiosError;

      if (!error.isAxiosError) {
        notificationError(err);
      }
    }
  };

  const handleVehicleSubmit = async () => {
    try {
      const values = await vehicleForm.validateFields();

      let image = values.image;

      if (!!values.imageUpload) {
        const result = await uploadArchive({
          file: [values.imageUpload as Blob],
          isPublic: false,
        });

        image = result.filePath;
      }

      const obj: CreateCustomerVehicleRequest = {
        model: {
          id: values.modelId,
          brand: { id: values.brandId, image: values.model?.brand.image },
        },
        fuel: { id: values.fuelId },
        color: { id: values.colorId },
        licensePlate: values.licensePlate,
        yearFabrication: Number(values.yearFabrication),
        yearModel: Number(values.yearModel),
        chassi: values.chassi,
        fleet: values.fleet,
        km: values.km,
        renavam: values.renavam,
        type: values?.type,
        active: true,
        accessories: values.accessories
          ? values.accessories.map((accessory) => parseAccessoryId(accessory))
          : [],
        image,
        cylinders: values.cylinders,
        engineCapacity: values.engineCapacity,
        engineName: values.engineName,
        engineTechnicalCode: values.engineTechnicalCode,
        horsePower: values.horsePower,
        release: values.release,
        valves: values.valves,
        line: values.line,
        airIntake: values.airIntake,
        rearBrake: values.rearBrake,
        frontBrake: values.frontBrake,
        brakeSystem: values.brakeSystem,
        steeringSystem: values.steeringSystem,
        tractionSystem: values.tractionSystem,
        injectionSystem: values.injectionSystem,
        transmissionType: values.transmissionType,
        engineConfiguration: values.engineConfiguration,
      };

      let result;

      if (vehicleId) {
        result = await updateVehicle.mutateAsync({ ...obj, id: vehicleId });
      } else {
        result = await createVehicle.mutateAsync(obj);
      }

      if (result) {
        setVehicleId(result.id);
      }
    } catch {}
  };

  const handleCancel = () => {
    setCustomerId(undefined);
    setVehicleId(undefined);
    customerForm.resetFields();
    vehicleForm.resetFields();
    onCancel();
  };

  const handleConfirm = async () => {
    const submit = async () => {
      if (vehicleId) {
        await updateWorkorder.mutateAsync({
          companyId: workorder?.company.id,
          vehicleId,
        });
      } else {
        await updateWorkorder.mutateAsync({
          companyId: workorder?.company.id,
          customerId,
        });
      }

      handleCancel();
      onConfirm();
    };

    if (hasVehicleData && !vehicleId) {
      Modal.confirm({
        title: 'Veículo não foi cadastrado',
        content:
          'Os dados do veículo informado não foram salvos, deseja finalizar o cadastro rápido do cliente sem o vinculo com o veículo?',
        okText: 'Salvar sem vincular',
        onOk: submit,
      });
    } else {
      await submit();
    }
  };

  return (
    <Modal
      open={visible}
      onCancel={handleCancel}
      onOk={handleConfirm}
      okButtonProps={{ disabled: !customerId && !vehicleId }}
      confirmLoading={updateWorkorder.isLoading}
      okText="Vincular cliente"
      title={intl.formatMessage({ id: 'customers.simplifiedRegister' })}
      width={1200}
    >
      <Group position="between" style={{ marginBottom: 24 }}>
        <Title style={{ margin: 0 }} level={4}>
          Dados do cliente
        </Title>

        <Button
          type="primary"
          onClick={handleCustomerSubmit}
          loading={createCustomer.isLoading || updateCustomer.isLoading}
        >
          Salvar
        </Button>
      </Group>

      <CustomerSimplifiedForm form={customerForm} />

      <CustomerContactCollapse customerId={customerId} />

      <Divider />

      <Group position="between" style={{ marginBottom: 24 }}>
        <Title style={{ margin: 0 }} level={4}>
          Dados do veículo
        </Title>

        {!!customerId && (
          <Button
            type="primary"
            onClick={handleVehicleSubmit}
            loading={createVehicle.isLoading || updateVehicle.isLoading}
            disabled={vehicleDisabled}
          >
            Salvar
          </Button>
        )}
      </Group>

      {!customerId && (
        <Empty
          image={<FaCar size={100} />}
          description="Necessário salvar o cliente primeiro"
          style={{ padding: 24 }}
        />
      )}

      {!!customerId && (
        <VehicleFormWrapper
          form={vehicleForm}
          disabledFields={false}
          customerId={customerId}
          onChangeAlert={(status) => {
            if (status !== undefined) {
              setVehicleDisabled(true);
            } else {
              setVehicleDisabled(false);
            }
          }}
        />
      )}
    </Modal>
  );
}

export default CustomerWorkorderCreateModal;
